import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  CheckCircle2,
  Cloud,
  Database,
  FileCheck2,
  FileText,
  Fingerprint,
  Globe2,
  KeyRound,
  Layers3,
  LockKeyhole,
  Mail,
  Network,
  ServerCog,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import { Link } from "react-router";
import { getTranslationFn } from "../../i18n";
import type { Locale } from "../../lib/locale";
import * as Paths from "../../paths";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Button } from "../ui/button";
import { MarketingLayout } from "./marketing-layout";

interface SecurityPageProps {
  locale: Locale;
}

type TranslationKey = Parameters<ReturnType<typeof getTranslationFn>>[0];

interface IconItem {
  icon: LucideIcon;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
}

interface ProofItem {
  icon: LucideIcon;
  labelKey: TranslationKey;
  valueKey: TranslationKey;
}

export function SecurityPage({ locale }: SecurityPageProps) {
  const t = getTranslationFn(locale);

  const navItems = [
    { label: t("Security.nav.protection"), href: "#protection" },
    { label: t("Security.nav.controls"), href: "#controls" },
    { label: t("Security.nav.ai"), href: "#ai-privacy" },
    { label: t("Security.nav.faq"), href: "#faq" },
  ];

  const proofPoints: ProofItem[] = [
    {
      icon: Globe2,
      labelKey: "Security.proof.eu.label",
      valueKey: "Security.proof.eu.value",
    },
    {
      icon: ShieldCheck,
      labelKey: "Security.proof.training.label",
      valueKey: "Security.proof.training.value",
    },
    {
      icon: LockKeyhole,
      labelKey: "Security.proof.encryption.label",
      valueKey: "Security.proof.encryption.value",
    },
    {
      icon: UserCheck,
      labelKey: "Security.proof.admin.label",
      valueKey: "Security.proof.admin.value",
    },
  ];

  const pillars: IconItem[] = [
    {
      icon: Globe2,
      titleKey: "Security.pillar.residency.title",
      bodyKey: "Security.pillar.residency.body",
    },
    {
      icon: ShieldCheck,
      titleKey: "Security.pillar.zeroTraining.title",
      bodyKey: "Security.pillar.zeroTraining.body",
    },
    {
      icon: Layers3,
      titleKey: "Security.pillar.isolation.title",
      bodyKey: "Security.pillar.isolation.body",
    },
    {
      icon: KeyRound,
      titleKey: "Security.pillar.encryption.title",
      bodyKey: "Security.pillar.encryption.body",
    },
    {
      icon: ServerCog,
      titleKey: "Security.pillar.enterprise.title",
      bodyKey: "Security.pillar.enterprise.body",
    },
  ];

  const controlGroups = [
    {
      icon: FileCheck2,
      titleKey: "Security.controls.application.title",
      items: [
        "Security.controls.application.item1",
        "Security.controls.application.item2",
        "Security.controls.application.item3",
      ],
    },
    {
      icon: Database,
      titleKey: "Security.controls.data.title",
      items: [
        "Security.controls.data.item1",
        "Security.controls.data.item2",
        "Security.controls.data.item3",
      ],
    },
    {
      icon: Cloud,
      titleKey: "Security.controls.infrastructure.title",
      items: [
        "Security.controls.infrastructure.item1",
        "Security.controls.infrastructure.item2",
        "Security.controls.infrastructure.item3",
      ],
    },
    {
      icon: Network,
      titleKey: "Security.controls.network.title",
      items: [
        "Security.controls.network.item1",
        "Security.controls.network.item2",
        "Security.controls.network.item3",
      ],
    },
    {
      icon: Fingerprint,
      titleKey: "Security.controls.organization.title",
      items: [
        "Security.controls.organization.item1",
        "Security.controls.organization.item2",
        "Security.controls.organization.item3",
      ],
    },
    {
      icon: UserCheck,
      titleKey: "Security.controls.admin.title",
      items: [
        "Security.controls.admin.item1",
        "Security.controls.admin.item2",
        "Security.controls.admin.item3",
      ],
    },
  ] satisfies {
    icon: LucideIcon;
    titleKey: TranslationKey;
    items: TranslationKey[];
  }[];

  const aiCommitments: IconItem[] = [
    {
      icon: ShieldCheck,
      titleKey: "Security.ai.noTraining.title",
      bodyKey: "Security.ai.noTraining.body",
    },
    {
      icon: Globe2,
      titleKey: "Security.ai.euProcessing.title",
      bodyKey: "Security.ai.euProcessing.body",
    },
    {
      icon: FileCheck2,
      titleKey: "Security.ai.dpa.title",
      bodyKey: "Security.ai.dpa.body",
    },
  ];

  const procurementItems: IconItem[] = [
    {
      icon: FileText,
      titleKey: "Security.procurement.dpa.title",
      bodyKey: "Security.procurement.dpa.body",
    },
    {
      icon: ServerCog,
      titleKey: "Security.procurement.privateCloud.title",
      bodyKey: "Security.procurement.privateCloud.body",
    },
    {
      icon: Mail,
      titleKey: "Security.procurement.review.title",
      bodyKey: "Security.procurement.review.body",
    },
  ];

  const faqItems = [
    ["Security.faq.q1", "Security.faq.a1"],
    ["Security.faq.q2", "Security.faq.a2"],
    ["Security.faq.q3", "Security.faq.a3"],
    ["Security.faq.q4", "Security.faq.a4"],
    ["Security.faq.q5", "Security.faq.a5"],
    ["Security.faq.q6", "Security.faq.a6"],
  ] satisfies [TranslationKey, TranslationKey][];

  return (
    <MarketingLayout locale={locale} navItems={navItems}>
      <section className="relative overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_20%_20%,rgba(14,89,56,0.12),transparent_28%),linear-gradient(135deg,#ffffff_0%,#f8fafc_48%,#eefcf4_100%)] py-20 md:py-28">
        <div className="container relative mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.72fr)] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white px-4 py-2 text-sm font-medium text-brand-700 shadow-sm">
                <ShieldCheck className="h-4 w-4" />
                {t("Security.hero.eyebrow")}
              </div>
              <h1 className="mt-8 max-w-4xl text-4xl font-semibold leading-[1.05] tracking-tight text-slate-950 md:text-6xl">
                {t("Security.hero.title")}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 md:text-xl">
                {t("Security.hero.description")}
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="h-12 rounded-full bg-brand-600 px-6 text-white shadow-lg shadow-brand-600/20 hover:bg-brand-700"
                >
                  <Link to="mailto:hello@anylawyer.com">
                    {t("Security.hero.primaryCta")}
                    <Mail className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="h-12 rounded-full border-slate-300 bg-white px-6 text-slate-800 hover:bg-slate-50"
                >
                  <Link to={Paths.docsPage("bezpieczenstwo-i-prywatnosc")}>
                    {t("Security.hero.secondaryCta")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white/90 p-3 shadow-2xl shadow-slate-950/10 backdrop-blur">
              <div className="rounded-md border border-slate-200 bg-slate-950 p-5 text-white">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-200">
                      {t("Security.hero.panelLabel")}
                    </p>
                    <p className="mt-2 text-lg font-semibold">
                      {t("Security.hero.panelTitle")}
                    </p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-brand-300" />
                </div>
                <div className="mt-5 grid gap-3">
                  {proofPoints.map((item) => (
                    <div
                      key={item.labelKey}
                      className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3 rounded-md border border-white/10 bg-white/[0.04] p-3"
                    >
                      <item.icon className="mt-0.5 h-5 w-5 text-brand-300" />
                      <div>
                        <p className="text-xs text-slate-400">
                          {t(item.labelKey)}
                        </p>
                        <p className="text-sm font-medium text-white">
                          {t(item.valueKey)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="protection" className="py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-700">
              {t("Security.pillars.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              {t("Security.pillars.title")}
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              {t("Security.pillars.description")}
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {pillars.map((item, index) => (
              <div
                key={item.titleKey}
                className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <item.icon className="h-6 w-6 text-brand-600" />
                  <span className="font-mono text-sm text-slate-300">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 text-base font-semibold text-slate-950">
                  {t(item.titleKey)}
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {t(item.bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="controls"
        className="border-y border-slate-200 bg-slate-50 py-20 md:py-24"
      >
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-700">
                {t("Security.controls.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
                {t("Security.controls.title")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                {t("Security.controls.description")}
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {controlGroups.map((group) => (
                <div
                  key={group.titleKey}
                  className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-50 text-brand-700">
                      <group.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-base font-semibold text-slate-950">
                      {t(group.titleKey)}
                    </h3>
                  </div>
                  <ul className="mt-5 space-y-3">
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className="flex gap-3 text-sm leading-6 text-slate-600"
                      >
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-brand-600" />
                        <span>{t(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="ai-privacy" className="py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-700">
                {t("Security.ai.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
                {t("Security.ai.title")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                {t("Security.ai.description")}
              </p>
            </div>
            <div className="grid gap-4">
              {aiCommitments.map((item) => (
                <div
                  key={item.titleKey}
                  className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-brand-700">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-slate-950">
                        {t(item.titleKey)}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {t(item.bodyKey)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-700">
                {t("Security.procurement.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
                {t("Security.procurement.title")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                {t("Security.procurement.description")}
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {procurementItems.map((item) => (
                <div
                  key={item.titleKey}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-5"
                >
                  <item.icon className="h-6 w-6 text-brand-600" />
                  <h3 className="mt-5 text-base font-semibold text-slate-950">
                    {t(item.titleKey)}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {t(item.bodyKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-slate-50 py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-700">
              {t("Security.faq.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              {t("Security.faq.title")}
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              {t("Security.faq.description")}
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            className="mx-auto mt-12 max-w-4xl rounded-lg border border-slate-200 bg-white px-5 shadow-sm md:px-8"
          >
            {faqItems.map(([question, answer], index) => (
              <AccordionItem
                key={question}
                value={`item-${index}`}
                className="border-slate-200"
              >
                <AccordionTrigger className="py-6 text-left text-base font-semibold text-slate-950 hover:no-underline">
                  {t(question)}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-7 text-slate-600">
                  {t(answer)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-brand-900 py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight text-white md:text-5xl">
              {t("Security.cta.title")}
            </h2>
            <p className="mt-5 text-lg leading-8 text-brand-100/80">
              {t("Security.cta.description")}
            </p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                asChild
                className="h-12 rounded-full bg-white px-6 text-brand-900 hover:bg-brand-50"
              >
                <Link to="mailto:hello@anylawyer.com">
                  {t("Security.cta.securityPacket")}
                  <Mail className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 rounded-full border-white/25 bg-transparent px-6 text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="mailto:hello@anylawyer.com">
                  {t("Security.cta.demo")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
